/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/cart/api';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { state } from '@/cart/lib/state';
import { CartModel } from '@/cart/data/models';
import { transformCart } from '@/cart/data/transforms';

import { GET_ESTIMATED_TOTALS_MUTATION } from './graphql/GetEstimatedTotalsMutation';

export interface EstimateAddressShippingInput {
  countryCode: string;
  postcode?: string;
  region?: {
    region?: string;
    id?: number;
  };
  shipping_method?: {
    carrier_code?: string;
    method_code?: string;
  };
}

export const getEstimatedTotals = async (
  address: EstimateAddressShippingInput
): Promise<CartModel | null> => {
  const cartId = state.cartId;

  if (!cartId) throw new Error('No cart ID found');
  if (!address) throw new Error('No address parameter found');

  const { countryCode, postcode, region } = address;
  const carrier_code = address.shipping_method?.carrier_code;
  const method_code = address.shipping_method?.method_code;

  return fetchGraphQl(GET_ESTIMATED_TOTALS_MUTATION, {
    variables: {
      cartId,
      address: {
        country_code: countryCode || 'US',
        postcode,
        region:
          region?.id !== undefined
            ? { region_id: region.id }
            : { region: region?.region ?? '' },
      },
      shipping_method: {
        carrier_code: carrier_code || '',
        method_code: method_code || '',
      },
    },
  }).then(({ errors, data }) => {
    if (errors) return handleFetchError(errors);

    const estimatedData: { cart: any } = data.estimateTotals;

    if (estimatedData) {
      const payload = transformCart(estimatedData.cart);

      return payload;
    }

    return null;
  });
};
